import React, { useEffect } from "react";
import { useForm, useFieldArray, FieldError } from "react-hook-form";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { INCOME } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import { addStepsData } from "app/lib/store/slices/steps/jointIncomeDetails";
import InputField from "app/components/common/inputs/InputField";
import RemoveItem from "app/components/common/buttons/RemoveItem";
import { useIncomeDetails } from "app/hooks/incomeDetails/useIncomeDetails";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import FormGroup from "app/components/common/inputs/FormGroup";
import SelectField from "app/components/common/inputs/SelectField";
import InfoTitle from "app/components/common/typography/InfoTitle";
import AddMore from "app/components/common/buttons/AddMore";
import { JointIncomeDetailsPartOneFromOne } from "app/utils/types";
import {
  frequencyOptions,
  otherIncomeOptions,
  propertyTypeOptions,
  holdersOptions,
} from "app/utils/staticValues";
import IncomeInputs from "app/components/common/inputs/IncomeInputs";
import AutocompleteLocations from "app/components/common/AutocompleteLocations";
import { normalizeIncome } from "app/utils/normalizeIncome";
import { useDeleteItem } from "app/hooks/useDeleteItem";

const IncomeDetailsPartOne: React.FC = () => {
  const { mutate: deleteItem, isPending: deleteIsPending } = useDeleteItem();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { income } = useSelector(
    (state: RootState) => state?.jointIncomeDetails?.stepsData
  );
  const { mutateAsync: submitIncome, isPending } = useIncomeDetails();
  const savedIds = useSelector(
    (state: RootState) => state?.loanTypesSteps?.savedIds
  );
  const { YourDetails, Coborrower } = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    register,
    setValue,
    setError,
    clearErrors,
  } = useForm<JointIncomeDetailsPartOneFromOne>({
    defaultValues: {
      yourDetails: {
        income: [
          { type: "gross", amount: "", frequency: "monthly" },
          {
            type: "overtime_shift_allowance",
            amount: "0",
            frequency: "monthly",
          },
          { type: "bonus_commission", amount: "0", frequency: "monthly" },
          { type: "gross_secondary", amount: "0", frequency: "monthly" },
        ],
      },
      coBorrowerDetails: {
        income: [
          { type: "gross", amount: "", frequency: "monthly" },
          {
            type: "overtime_shift_allowance",
            amount: "0",
            frequency: "monthly",
          },
          { type: "bonus_commission", amount: "0", frequency: "monthly" },
          { type: "gross_secondary", amount: "0", frequency: "monthly" },
        ],
      },
    },
  });
  const yourIncomeTypes = watch("yourDetails.income");
  const coIncomeTypes = watch("coBorrowerDetails.income");

  const onSubmit = async (data: JointIncomeDetailsPartOneFromOne) => {
    if (!savedIds.applicantID && savedIds?.secondApplicantID) {
      toast.error("Applicant id required");
      return;
    }
    const { yourDetails, coBorrowerDetails } = data;
    const yourDetailsData = yourDetails.income.map((item) => ({
      ...(item.dbId && { id: item.dbId }),

      ...(item.type === "rental_income" &&
        item.noOfTitleHolders && {
          noOfTitleHolders: Number(item.noOfTitleHolders),
        }),
      ...(item.type === "rental_income" &&
        item.propertyExpense && {
          propertyExpense: Number(item.propertyExpense),
        }),
      ...(item.type === "rental_income" &&
        item.propertyType && {
          propertyType: item.propertyType,
        }),
      ...(item.type === "rental_income" &&
        item.propertyAddress && {
          propertyAddress: item.propertyAddress,
        }),
      type: item.type,
      amount: Number(item.amount),
      frequency: item.frequency,
    }));

    const coBorrowerDetailsData = coBorrowerDetails.income.map((item) => ({
      ...(item.dbId && { id: item.dbId }),

      ...(item.type === "rental_income" &&
        item.noOfTitleHolders && {
          noOfTitleHolders: Number(item.noOfTitleHolders),
        }),
      ...(item.type === "rental_income" &&
        item.propertyExpense && {
          propertyExpense: Number(item.propertyExpense),
        }),
      ...(item.type === "rental_income" &&
        item.propertyType && {
          propertyType: item.propertyType,
        }),
      ...(item.type === "rental_income" &&
        item.propertyAddress && {
          propertyAddress: item.propertyAddress,
        }),

      type: item.type,
      amount: Number(item.amount),
      frequency: item.frequency,
    }));

    const request1 = {
      url: `${INCOME}/${savedIds.applicantID}`,
      data: { income: yourDetailsData },
    };
    const request2 = {
      url: `${INCOME}/${savedIds?.secondApplicantID}`,
      data: { income: coBorrowerDetailsData },
    };

    try {
      const [response1, response2] = await Promise.all([
        submitIncome(request1),
        submitIncome(request2),
      ]);

      if (response1 && response2) {
        const yourDetailsData = response1?.savedIncomes?.map((item: any) => ({
          dbId: item.id,
          type: item.type,
          amount: item.amount,
          frequency: item.frequency,
          propertyType: item.propertyType,
          propertyAddress: item.propertyAddress,
          propertyExpense: item.propertyExpense,
          noOfTitleHolders: String(item.noOfTitleHolders),
        }));
        const coBorrowerDetailsData = response2?.savedIncomes?.map(
          (item: any) => ({
            dbId: item.id,
            type: item.type,
            amount: item.amount,
            frequency: item.frequency,
            propertyType: item.propertyType,
            propertyAddress: item.propertyAddress,
            propertyExpense: item.propertyExpense,
            noOfTitleHolders: String(item.noOfTitleHolders),
          })
        );
        const savedData = {
          yourDetails: yourDetailsData,
          coBorrowerDetails: coBorrowerDetailsData,
        };
        dispatch(addStepsData({ income: savedData }));
        navigate(`/joint-assets-liabilities`);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "yourDetails.income",
  });
  const {
    fields: cofields,
    append: coappend,
    remove: coremove,
  } = useFieldArray({
    control,
    name: "coBorrowerDetails.income",
  });

  useEffect(() => {
    if (income) {
      reset({
        yourDetails: {
          income: normalizeIncome(income.yourDetails || []),
        },
        coBorrowerDetails: {
          income: normalizeIncome(income.coBorrowerDetails || []),
        },
      });
    } else {
      const yourGrossFromApplicant = income?.yourDetails.find(
        (item: any) => item.type === "gross"
      );
      const coGrossFromApplicant = income?.coBorrowerDetails?.find(
        (item: any) => item.type === "gross"
      );

      if (yourGrossFromApplicant) {
        reset({
          yourDetails: {
            income: [
              {
                type: "gross",
                dbId: yourGrossFromApplicant.id,
                amount: String(yourGrossFromApplicant.amount || "0"),
                frequency: yourGrossFromApplicant.frequency || "monthly",
              },
              {
                type: "overtime_shift_allowance",
                amount: "0",
                frequency: "monthly",
              },
              { type: "bonus_commission", amount: "0", frequency: "monthly" },
              { type: "gross_secondary", amount: "0", frequency: "monthly" },
            ],
          },
          coBorrowerDetails: {
            income: [
              {
                type: "gross",
                dbId: coGrossFromApplicant?.id,
                amount: String(coGrossFromApplicant?.amount || "0"),
                frequency: coGrossFromApplicant?.frequency || "monthly",
              },
              {
                type: "overtime_shift_allowance",
                amount: "0",
                frequency: "monthly",
              },
              { type: "bonus_commission", amount: "0", frequency: "monthly" },
              { type: "gross_secondary", amount: "0", frequency: "monthly" },
            ],
          },
        });
      }
    }
  }, [income, reset]);

  const handlePrevious = () => {
    navigate(`/joint-employment-details`);
  };

  return (
    <>
      <ProgressBar progress={0} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Income Details" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-5 flex-col md:flex-col xl:flex-row sm:gap-4 ">
            <div className="flex-1">
              <InfoTitle title="Your Details" />
              {fields?.map((field, index) => {
                const isRentalIncome =
                  yourIncomeTypes[index]?.type === "rental_income";
                const selectedTypes = yourIncomeTypes
                  .filter((_, i) => i !== index)
                  .map((item) => item.type)
                  .filter((type) => type && type !== "rental_income");

                const filteredOptions = otherIncomeOptions.filter(
                  (option) => !selectedTypes.includes(option.value)
                );
                return (
                  <React.Fragment key={field.id}>
                    {index < 4 ? (
                      <>
                        <FormGroup className="relative">
                          <IncomeInputs
                            label={
                              index === 0
                                ? "Gross Salary/Income"
                                : index === 1
                                ? "Overtime/shift allowance"
                                : index === 2
                                ? "Bonus/Commission"
                                : "Gross secondary/Business income"
                            }
                            name={`yourDetails.income.${index}.amount`}
                            control={control}
                            type="number"
                            required={index === 0 ? true : false}
                            placeholder="$200,000"
                            error={
                              errors.yourDetails?.income?.[index]?.amount
                                ?.message
                            }
                            className="flex-1"
                            rules={{
                              validate: (value: string | number) => {
                                if (
                                  value === "" ||
                                  value === null ||
                                  value === undefined
                                )
                                  return "Amount is required";
                                if (Number(value) < 0)
                                  return "Value cannot be negative";
                                return true;
                              },
                            }}
                            selectlabel="frequency"
                            selectname={`yourDetails.income.${index}.frequency`}
                            selectoptions={frequencyOptions}
                            selectplaceholder={"Monthly"}
                            selectrequired={true}
                            selecterror={
                              errors.yourDetails?.income?.[index]?.frequency
                                ?.message
                            }
                          />
                        </FormGroup>
                      </>
                    ) : (
                      <>
                        <FormGroup className="relative ">
                          <SelectField
                            label="Other"
                            name={`yourDetails.income.${index}.type`}
                            control={control}
                            // options={otherIncomeOptions}
                            options={filteredOptions}
                            placeholder="Government benefits"
                            error={
                              (
                                errors?.yourDetails?.income?.[index]
                                  ?.type as FieldError
                              )?.message
                            }
                            required={true}
                            className="flex-1"
                            isDisabled={!!field?.dbId}
                            removeGrayBg={true}
                          />

                          <div className="flex-1">
                            <label className="absolute block font-Mulish text-base font-normal text-lightBlue mb-1">
                              Income
                            </label>
                            <IncomeInputs
                              label={"Income"}
                              hidelabel={true}
                              name={`yourDetails.income.${index}.amount`}
                              control={control}
                              type="number"
                              required={true}
                              placeholder="$1000"
                              error={
                                errors.yourDetails?.income?.[index]?.amount
                                  ?.message
                              }
                              className="flex-1 mt-7 md:mt-7 sm:mt-7 lg:mt-0"
                              rules={{
                                min: {
                                  value: 1,
                                  message: "value must be greater than 0",
                                },
                              }}
                              selectlabel="frequency"
                              selectname={`yourDetails.income.${index}.frequency`}
                              selecterror={
                                errors.yourDetails?.income?.[index]?.frequency
                                  ?.message
                              }
                              selectoptions={frequencyOptions}
                              selectplaceholder={"monthly"}
                              customWidth={true}
                              selectrequired={true}
                            />
                          </div>
                          {index > 3 && (
                            <RemoveItem
                              disabled={deleteIsPending}
                              className="absolute right-0 "
                              onClick={() => {
                                if (field?.dbId) {
                                  deleteItem(
                                    `/income/${savedIds.applicantID}/incomes/${field?.dbId}`,
                                    {
                                      onSuccess: () => {
                                        remove(index);
                                        const updatedYourIncome =
                                          income?.yourDetails?.filter(
                                            (item: any) =>
                                              item?.dbId !== field?.dbId
                                          ) || [];
                                        dispatch(
                                          addStepsData({
                                            income: {
                                              ...income,
                                              yourDetails: updatedYourIncome,
                                            },
                                          })
                                        );
                                      },
                                    }
                                  );
                                } else {
                                  remove(index);
                                }
                              }}
                            />
                          )}
                        </FormGroup>
                        {isRentalIncome && (
                          <>
                            <FormGroup>
                              <SelectField
                                label="Property Type"
                                name={`yourDetails.income.${index}.propertyType`}
                                control={control}
                                options={propertyTypeOptions}
                                placeholder="Apartment"
                                error={
                                  errors.yourDetails?.income?.[index]
                                    ?.propertyType?.message
                                }
                                required={true}
                                className="flex-1"
                              />
                              <SelectField
                                label="No. of title holders"
                                name={`yourDetails.income.${index}.noOfTitleHolders`}
                                control={control}
                                options={holdersOptions}
                                placeholder="2"
                                error={
                                  errors.yourDetails?.income?.[index]
                                    ?.noOfTitleHolders?.message as
                                    | string
                                    | undefined
                                }
                                required={true}
                                className="flex-1"
                              />
                            </FormGroup>
                            <FormGroup>
                              <InputField
                                label={`Property Expense`}
                                name={`yourDetails.income.${index}.propertyExpense`}
                                control={control}
                                type="number"
                                required={false}
                                placeholder="$1000"
                                error={
                                  errors.yourDetails?.income?.[index]
                                    ?.propertyExpense?.message as
                                    | string
                                    | undefined
                                }
                                className="flex-1"
                                // rules={{
                                //   min: {
                                //     value: 1,
                                //     message: "value must be greater than 0",
                                //   },
                                // }}
                                rules={{
                                  validate: (value: any) => {
                                    const amount = watch(
                                      `yourDetails.income.${index}.amount`
                                    );
                                    if (!value) return true;
                                    if (Number(value) > Number(amount)) {
                                      return "Property expense cannot be greater than income amount";
                                    }
                                    return true;
                                  },
                                }}
                              />
                              <AutocompleteLocations
                                FieldName={`yourDetails.income.${index}.propertyAddress`}
                                placeholder="2464 Royal Ln. Mesa..."
                                register={register}
                                setValue={setValue}
                                setError={setError}
                                label="Property address"
                                required={true}
                                className="flex-1"
                                error={
                                  errors.yourDetails?.income?.[index]
                                    ?.propertyAddress?.message as
                                    | string
                                    | undefined
                                }
                                // stateFieldName="employerAddress.state"
                                // postCodeFieldName="employerAddress.postCode"
                                clearErrors={clearErrors}
                              />
                            </FormGroup>
                          </>
                        )}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
              {fields.length < 11 && (
                <div className="flex items-center justify-end mt-6 sm:mb-0 mb-6">
                  <AddMore
                    onClick={() =>
                      append({ type: "", amount: "", frequency: "" })
                    }
                  />
                </div>
              )}
            </div>
            <div className="flex-1">
              <InfoTitle title="Co-borrower" />
              {cofields?.map((field, index) => {
                const isRentalIncome =
                  coIncomeTypes[index]?.type === "rental_income";
                const selectedTypes = coIncomeTypes
                  .filter((_, i) => i !== index)
                  .map((item) => item.type)
                  .filter((type) => type && type !== "rental_income");

                const filteredOptions = otherIncomeOptions.filter(
                  (option) => !selectedTypes.includes(option.value)
                );
                return (
                  <React.Fragment key={field.id}>
                    {index < 4 ? (
                      <>
                        <FormGroup className="relative">
                          <IncomeInputs
                            label={
                              index === 0
                                ? "Gross Salary/Income"
                                : index === 1
                                ? "Overtime/shift allowance"
                                : index === 2
                                ? "Bonus/Commission"
                                : "Gross secondary/Business income"
                            }
                            name={`coBorrowerDetails.income.${index}.amount`}
                            control={control}
                            type="number"
                            required={true}
                            placeholder="$200,000"
                            error={
                              errors.coBorrowerDetails?.income?.[index]?.amount
                                ?.message
                            }
                            className="flex-1"
                            rules={{
                              validate: (value: string | number) => {
                                if (
                                  value === "" ||
                                  value === null ||
                                  value === undefined
                                )
                                  return "Amount is required";
                                if (Number(value) < 0)
                                  return "Value cannot be negative";
                                return true;
                              },
                            }}
                            selectlabel="frequency"
                            selectname={`coBorrowerDetails.income.${index}.frequency`}
                            selectoptions={frequencyOptions}
                            selectplaceholder={"Monthly"}
                            selectrequired={true}
                            selecterror={
                              errors.coBorrowerDetails?.income?.[index]
                                ?.frequency?.message
                            }
                          />
                        </FormGroup>
                      </>
                    ) : (
                      <>
                        <FormGroup className="relative ">
                          <SelectField
                            label="Other"
                            name={`coBorrowerDetails.income.${index}.type`}
                            control={control}
                            // options={otherIncomeOptions}
                            options={filteredOptions}
                            placeholder="Government benefits"
                            error={
                              (
                                errors?.coBorrowerDetails?.income?.[index]
                                  ?.type as FieldError
                              )?.message
                            }
                            required={true}
                            className="flex-1"
                            isDisabled={!!field?.dbId}
                            removeGrayBg={true}
                          />
                          <div className="flex-1">
                            <label className="absolute block font-Mulish text-base font-normal text-lightBlue mb-1">
                              Income
                            </label>
                            <IncomeInputs
                              label={"Income"}
                              hidelabel={true}
                              name={`coBorrowerDetails.income.${index}.amount`}
                              control={control}
                              type="number"
                              required={true}
                              placeholder="$1000"
                              error={
                                errors.coBorrowerDetails?.income?.[index]
                                  ?.amount?.message
                              }
                              className="flex-1 mt-7 md:mt-7 sm:mt-7 lg:mt-0"
                              rules={{
                                min: {
                                  value: 1,
                                  message: "value must be greater than 0",
                                },
                              }}
                              selectlabel="frequency"
                              selectname={`coBorrowerDetails.income.${index}.frequency`}
                              selectoptions={frequencyOptions}
                              selectplaceholder={"monthly"}
                              selecterror={
                                errors.coBorrowerDetails?.income?.[index]
                                  ?.frequency?.message
                              }
                              customWidth={true}
                              selectrequired={true}
                            />
                          </div>
                          {index > 3 && (
                            <RemoveItem
                              disabled={deleteIsPending}
                              className="absolute right-0 "
                              onClick={() => {
                                if (field?.dbId) {
                                  deleteItem(
                                    `/income/${savedIds?.secondApplicantID}/incomes/${field?.dbId}`,
                                    {
                                      onSuccess: () => {
                                        coremove(index);
                                        const updatedCoDetails =
                                          income?.coBorrowerDetails?.filter(
                                            (item: any) =>
                                              item?.dbId !== field?.dbId
                                          ) || [];
                                        dispatch(
                                          addStepsData({
                                            income: {
                                              ...income,
                                              coBorrowerDetails:
                                                updatedCoDetails,
                                            },
                                          })
                                        );
                                      },
                                    }
                                  );
                                } else {
                                  coremove(index);
                                }
                              }}
                            />
                          )}
                        </FormGroup>

                        {isRentalIncome && (
                          <>
                            <FormGroup>
                              <SelectField
                                label="Property Type"
                                name={`coBorrowerDetails.income.${index}.propertyType`}
                                control={control}
                                options={propertyTypeOptions}
                                placeholder="Apartment"
                                error={
                                  errors.coBorrowerDetails?.income?.[index]
                                    ?.propertyType?.message
                                }
                                required={true}
                                className="flex-1"
                              />
                              <SelectField
                                label="No. of title holders"
                                name={`coBorrowerDetails.income.${index}.noOfTitleHolders`}
                                control={control}
                                options={holdersOptions}
                                placeholder="2"
                                error={
                                  errors.coBorrowerDetails?.income?.[index]
                                    ?.noOfTitleHolders?.message as
                                    | string
                                    | undefined
                                }
                                required={true}
                                className="flex-1"
                              />
                            </FormGroup>
                            <FormGroup>
                              <InputField
                                label={`Property Expense`}
                                name={`coBorrowerDetails.income.${index}.propertyExpense`}
                                control={control}
                                type="number"
                                required={false}
                                placeholder="$1000"
                                error={
                                  errors.coBorrowerDetails?.income?.[index]
                                    ?.propertyExpense?.message as
                                    | string
                                    | undefined
                                }
                                className="flex-1"
                                // rules={{
                                //   min: {
                                //     value: 1,
                                //     message: "value must be greater than 0",
                                //   },
                                // }}
                                rules={{
                                  validate: (value: any) => {
                                    const amount = watch(
                                      `coBorrowerDetails.income.${index}.amount`
                                    );
                                    if (!value) return true;
                                    if (Number(value) > Number(amount)) {
                                      return "Property expense cannot be greater than income amount";
                                    }
                                    return true;
                                  },
                                }}
                              />
                              <AutocompleteLocations
                                FieldName={`coBorrowerDetails.income.${index}.propertyAddress`}
                                placeholder="2464 Royal Ln. Mesa..."
                                register={register}
                                setValue={setValue}
                                setError={setError}
                                label="Property address"
                                required={true}
                                className="flex-1"
                                error={
                                  errors.coBorrowerDetails?.income?.[index]
                                    ?.propertyAddress?.message as
                                    | string
                                    | undefined
                                }
                                // stateFieldName="employerAddress.state"
                                // postCodeFieldName="employerAddress.postCode"
                                clearErrors={clearErrors}
                              />
                            </FormGroup>
                          </>
                        )}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
              {cofields.length < 11 && (
                <div className="flex items-center justify-end mt-6 sm:mb-0 mb-6">
                  <AddMore
                    onClick={() =>
                      coappend({ type: "", amount: "", frequency: "" })
                    }
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom onClick={handlePrevious}>Previous</PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default IncomeDetailsPartOne;
